import React from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { Box, Button, Dialog, H1, H3, HStack, ShimmerText, Spinner, Text, VStack } from '@betterment/samba-react';
import { ErrorBoundary, SambaAutoLink } from '@betterment/shared-components';
import { useFallbackKeys } from '@betterment/shared-components/src/hooks/useFallbackKeys';

import { AccountHighlight } from './AccountHighlight';
import { EditAssumptionsBanner, EditAssumptionsToggleTip } from './EditAssumptionsBanner';
import { GradientBorderBox } from './GradientBorderBox';
import { OpenAccountButton } from './OpenAccountButton';

import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import AiOrb from '../assets/icon_ai_orb.svg';
import { CreateAccountRecommendationDocument } from '../graphql/mutation.createAccountRecommendation.generated';
import {
  AccountRecommendationOptionDocument,
  RecommendedProductOnboardingOptionFragment,
} from '../graphql/query.accountRecommendationOption.generated';

type RecommendationContentProps = {
  initialRecommendation: RecommendedProductOnboardingOptionFragment;
};

const RecommendationContent: React.FC<RecommendationContentProps> = ({ initialRecommendation }) => {
  const [createAccountRecommendation, { data, loading }] = useGraphQLMutation(CreateAccountRecommendationDocument);
  const recommendationResult = data?.createAccountRecommendation?.result;
  const productOption = recommendationResult?.productOnboardingOption || initialRecommendation;
  const reasonsToBelieve = useFallbackKeys(productOption.reasonsToBelieve);

  return (
    <VStack css={{ gap: 'space.400' }}>
      <VStack css={{ gap: 'space.100' }}>
        <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
          <Text variant="smallCaps">RECOMMENDATION</Text>
          <EditAssumptionsToggleTip />
        </HStack>
        <H1 variant="brandTitle" color="text">
          {productOption.name}
        </H1>
        <Text variant="text100" color="text">
          {productOption.shortDescription}
        </Text>
        {reasonsToBelieve.map(({ item, key }) => (
          <AccountHighlight key={key} text={item} />
        ))}
      </VStack>
      {recommendationResult || loading ? (
        <VStack css={{ gap: 'space.200', width: '100%' }}>
          <GradientBorderBox style={{ width: '100%' }}>
            <VStack css={{ padding: 'space.200', gap: 'space.200', width: '100%' }}>
              <HStack css={{ gap: 'space.100', alignItems: 'center' }}>
                <img src={AiOrb} alt="AI Icon" width="20px" height="20px" />
                <H3 variant="title100" color="text">
                  Why this account?
                </H3>
              </HStack>
              {recommendationResult ? (
                <Text
                  variant="text50"
                  color="text"
                  dangerouslySetInnerHTML={{ __html: recommendationResult.reasoning }}
                />
              ) : (
                <VStack css={{ gap: 'space.100', width: '100%' }}>
                  <ShimmerText textStyle="text50" css={{ width: '100%' }} />
                  <ShimmerText textStyle="text50" css={{ width: '100%' }} />
                  <Box css={{ maxWidth: '300px' }}>
                    <ShimmerText textStyle="text50" />
                  </Box>
                </VStack>
              )}
              <Text variant="text25" color="text.weak">
                AI-summary based on your financial profile.{' '}
                <SambaAutoLink to="/foo" target="_blank">
                  Learn more
                </SambaAutoLink>
              </Text>
            </VStack>
          </GradientBorderBox>
          <EditAssumptionsBanner />
        </VStack>
      ) : (
        <VStack css={{ gap: 'space.200' }}>
          <GradientBorderBox borderRadius={20}>
            <Button
              variant="textSecondary"
              css={{ borderRadius: '20px', textDecoration: 'none', background: 'none', padding: '1px' }}
              onClick={() => createAccountRecommendation({ variables: { skippedProductOnboardingOptions: [] } })}
            >
              <HStack
                css={{
                  px: 'space.150',
                  py: 'space.100',
                  borderRadius: '20px',
                  background: 'background.surface',
                  gap: 'space.50',
                  alignItems: 'center',
                }}
              >
                <img src={AiOrb} alt="AI Icon" />
                <Text variant="text50" color="text" css={{ fontWeight: 'medium' }}>
                  Why this account?
                </Text>
              </HStack>
            </Button>
          </GradientBorderBox>
          <Text variant="text25">
            Generates an AI summary based on your financial profile. We never use your data to train our models.{' '}
            <SambaAutoLink to="/foo" target="_blank">
              Learn more
            </SambaAutoLink>
          </Text>
        </VStack>
      )}
      <OpenAccountButton option={initialRecommendation} />
    </VStack>
  );
};

const Content: React.FC = () => {
  const { data: optionData } = useGraphQLQuery(AccountRecommendationOptionDocument, { fetchPolicy: 'cache-first' });

  const initialRecommendation = optionData?.accountRecommendationOption?.recommendedProductOnboardingOption;

  if (initialRecommendation) return <RecommendationContent initialRecommendation={initialRecommendation} />;

  return (
    <Box css={{ justifyContent: 'center', alignItems: 'center', padding: 'space.400', minHeight: '500px' }}>
      <Spinner size="lg" />
    </Box>
  );
};

export const AccountRecommendationModal: React.FC = () => {
  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <GradientBorderBox borderRadius={20} role="button" tabIndex={0} style={{ width: '100%' }}>
          <HStack
            css={{
              px: 'space.200',
              py: 'space.100',
              gap: 'space.50',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <img src={AiOrb} alt="AI Icon" />
            <Text variant="text50" color="text" css={{ fontWeight: 'medium' }}>
              Get a personalized recommendation
            </Text>
          </HStack>
        </GradientBorderBox>
      </Dialog.Trigger>
      <Dialog.Content size="large" css={{ p: 'space.400', width: '60%', maxWidth: '800px' }}>
        <ErrorBoundary fallbackType="full" feature="account recommendation" owner="agents">
          <IdempotencyProviderWithFeatureRegistry featureName="createAccountRecommendation">
            <Content />
          </IdempotencyProviderWithFeatureRegistry>
        </ErrorBoundary>
      </Dialog.Content>
    </Dialog>
  );
};
